'use client'
import { useEffect, useState } from 'react'

import { API_ROUTES } from '@/config/constants'
import { getFormattedUrl, buildQueryString } from '@/helpers/url'

// We no longer need to pass tokens to this component
export interface ApiResponse {
  status: number
  data: Record<string, unknown>
}

export interface ErrorResponse {
  status: number
  message: string
}

export function useAuthenticatedData(
  route: string,
  params?: { [key: string]: string },
  queryParams?: { [key: string]: string }
) {
  const authenticatedEndpoint = API_ROUTES[route as keyof typeof API_ROUTES]

  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<ErrorResponse | null>(null)

  if (!authenticatedEndpoint) {
    throw new Error('Invalid route')
  }

  useEffect(() => {
    const checkAuth0User = async () => {
      setLoading(true)
      try {
        let endpoint = getFormattedUrl(authenticatedEndpoint, params)

        if (queryParams) {
          endpoint = buildQueryString(endpoint, queryParams)
        }

        // Call our server-side API route instead of the external API directly
        const response = await fetch(endpoint, {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })

        console.log('API Response Status:', response.status)

        if (!response.ok) {
          throw JSON.stringify({
            status: response.status,
            message: response.statusText || `Error: ${response.status}`,
          })
        }

        const { data } = await response.json()

        console.log('API Response Data:', data)

        setApiResponse(data)
      } catch (fetchError) {
        // console.error('Fetch error:', fetchError)
        setError(JSON.parse(fetchError as string))
      } finally {
        setLoading(false)
      }
    }

    // Call the API route immediately - tokens are handled server-side
    checkAuth0User()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedEndpoint])

  return {
    apiResponse,
    loading,
    error,
  }
}
